import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { announcementAPI } from '../../services/api';
import { FaBell, FaPlus, FaEdit, FaTrash, FaArrowLeft, FaThumbtack, FaClock } from 'react-icons/fa';
import Button from '../../components/Button';

const ManageAnnouncements = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, pinned, active, expired
    const navigate = useNavigate();

    useEffect(() => {
        loadAnnouncements();
    }, [filter]);

    const loadAnnouncements = async () => {
        try {
            setLoading(true);
            const response = await announcementAPI.getAll();
            let filtered = response.announcements || response || [];
            
            // Apply filters
            if (filter === 'pinned') {
                filtered = filtered.filter(a => a.is_pinned === 1 || a.is_pinned === true);
            } else if (filter === 'active') {
                filtered = filtered.filter(a => !a.expires_at || new Date(a.expires_at) > new Date());
            } else if (filter === 'expired') {
                filtered = filtered.filter(a => a.expires_at && new Date(a.expires_at) <= new Date());
            }
            
            setAnnouncements(filtered);
        } catch (err) {
            console.error('Failed to load announcements:', err);
            toast.error('Failed to load announcements. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await confirmAction('Are you sure you want to delete this announcement?');
        if (!confirmed) return;
        
        try {
            // Note: You'll need to add delete endpoint to announcementAPI
            await announcementAPI.delete(id);
            toast.success('Announcement deleted successfully!');
            loadAnnouncements();
        } catch (err) {
            console.error('Failed to delete announcement:', err);
            toast.error('Failed to delete announcement. Please try again.');
        }
    };

    const isExpired = (expiresAt) => {
        if (!expiresAt) return false;
        return new Date(expiresAt) <= new Date();
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="mb-6">
                    <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
                        <FaArrowLeft className="mr-2" /> Back to Dashboard
                    </Link>
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                <FaBell className="text-primary-600" />
                                Manage Announcements
                            </h1>
                            <p className="text-gray-600 mt-2">Create, edit, and manage system announcements</p>
                        </div>
                        <Button to="/admin/announcements/create" variant="primary-gradient" className="px-6 py-3 flex items-center gap-2">
                            <FaPlus /> Create Announcement
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex gap-4">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            All Announcements
                        </button>
                        <button
                            onClick={() => setFilter('pinned')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'pinned' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Pinned
                        </button>
                        <button
                            onClick={() => setFilter('active')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'active' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Active
                        </button>
                        <button
                            onClick={() => setFilter('expired')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                filter === 'expired' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Expired
                        </button>
                    </div>
                </div>

                {/* Announcements List */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : announcements.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                        <FaBell className="text-6xl text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No announcements found</h3>
                        <p className="text-gray-500 mb-6">Create your first announcement to get started</p>
                        <Button to="/admin/announcements/create" variant="primary-gradient" className="px-6 py-3">
                            <FaPlus className="inline mr-2" /> Create Announcement
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {announcements.map((announcement) => (
                            <div key={announcement.id} className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${
                                announcement.is_pinned === 1 || announcement.is_pinned === true ? 'border-yellow-500' : 'border-blue-500'
                            }`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            {(announcement.is_pinned === 1 || announcement.is_pinned === true) && (
                                                <FaThumbtack className="text-yellow-500" />
                                            )}
                                            <h3 className="text-xl font-bold text-gray-900">{announcement.title}</h3>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                announcement.type === 'urgent' ? 'bg-red-100 text-red-700' :
                                                announcement.type === 'general' ? 'bg-blue-100 text-blue-700' :
                                                'bg-gray-100 text-gray-700'
                                            }`}>
                                                {announcement.type || 'general'}
                                            </span>
                                            {isExpired(announcement.expires_at) && (
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                                                    Expired
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-gray-600 mb-3">{announcement.content}</p>
                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                            <span>Created: {new Date(announcement.created_at).toLocaleDateString()}</span>
                                            {announcement.expires_at && (
                                                <span className="flex items-center gap-1">
                                                    <FaClock /> Expires: {new Date(announcement.expires_at).toLocaleDateString()}
                                                </span>
                                            )}
                                            {announcement.target_audience && (
                                                <span>Target: {Array.isArray(announcement.target_audience) ? announcement.target_audience.join(', ') : 'All'}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                        <button
                                            onClick={() => navigate(`/admin/announcements/${announcement.id}/edit`)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(announcement.id)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageAnnouncements;

